import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, MailOpen, Plus, Download, TrendingUp, Users, MapPin, Package, StickyNote, Receipt, Check, Square, CheckSquare, X, FileText } from "lucide-react";
import { downloadReceipt } from "@/lib/receipt";

function Login({ onLogin }) {
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/auth/login", form);
            if (r.data.role !== "admin") {
                toast.error("Tento účet nemá práva majitele. Přihlaste se přes rozcestník.");
                setBusy(false);
                return;
            }
            localStorage.setItem("strokay_token", r.data.token);
            localStorage.setItem("strokay_email", r.data.email);
            localStorage.setItem("strokay_role", r.data.role);
            toast.success("Přihlášeno");
            onLogin();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Přihlášení selhalo");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-burgundyDeep px-6">
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md bg-cream p-10 border border-ink/15">
                <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink2 hover:text-burgundy mb-8">
                    ← zpět
                </Link>
                <p className="overline mb-3">Majitel</p>
                <h1 className="font-serif text-4xl text-ink mb-8">Přihlášení</h1>
                <label className="block mb-6">
                    <span className="overline text-ink2">Email</span>
                    <input
                        data-testid="admin-login-email"
                        type="email"
                        className="bottom-input mt-1"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                    />
                </label>
                <label className="block mb-8">
                    <span className="overline text-ink2">Heslo</span>
                    <input
                        data-testid="admin-login-password"
                        type="password"
                        className="bottom-input mt-1"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                    />
                </label>
                <button data-testid="admin-login-submit" type="submit" className="btn-burgundy w-full" disabled={busy}>
                    {busy ? "..." : "Přihlásit se"}
                </button>
            </form>
        </div>
    );
}

function Panel({ onLogout }) {
    const [tab, setTab] = useState("revenue");
    const [messages, setMessages] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [range, setRange] = useState();
    const [icalUrl, setIcalUrl] = useState("https://www.e-chalupy.cz/api/calendar/22195/48A9a2/default.ics");
    const [revenue, setRevenue] = useState(null);
    const [orders, setOrders] = useState([]);
    const [staff, setStaff] = useState([]);
    const [tables, setTables] = useState([]);
    const [extras, setExtras] = useState([]);
    const [notes, setNotes] = useState([]);
    const [newStaff, setNewStaff] = useState({ name: "", username: "", password: "", color: "#5B7CB7" });
    const [newExtra, setNewExtra] = useState({ name: "", price: "", category: "nápoje" });
    const [newNote, setNewNote] = useState("");
    const [wines, setWines] = useState([]);

    const load = useCallback(async () => {
        try {
            const [m, b, rev, o, s, tb, ex, n, w] = await Promise.all([
                api.get("/admin/messages"),
                api.get("/admin/bookings"),
                api.get("/admin/revenue"),
                api.get("/admin/orders?limit=100"),
                api.get("/admin/staff"),
                api.get("/tables"),
                api.get("/extras"),
                api.get("/admin/notes"),
                api.get("/wines"),
            ]);
            setMessages(m.data); setBookings(b.data); setRevenue(rev.data);
            setOrders(o.data); setStaff(s.data); setTables(tb.data);
            setExtras(ex.data); setNotes(n.data); setWines(w.data);
        } catch (err) {
            if (err.response?.status === 401) onLogout();
        }
    }, [onLogout]);

    useEffect(() => { load(); }, [load]);

    const markRead = async (id) => {
        await api.patch(`/admin/messages/${id}`);
        load();
    };
    const delMsg = async (id) => {
        if (!window.confirm("Smazat zprávu?")) return;
        await api.delete(`/admin/messages/${id}`);
        load();
    };

    const addBooking = async () => {
        if (!range?.from || !range?.to) {
            toast.error("Vyberte rozsah dat");
            return;
        }
        const fmt = (d) => d.toISOString().slice(0, 10);
        try {
            await api.post("/admin/bookings", {
                start_date: fmt(range.from),
                end_date: fmt(range.to),
                summary: "Obsazeno",
            });
            toast.success("Termín přidán");
            setRange(undefined);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const delBooking = async (id) => {
        if (!window.confirm("Smazat termín?")) return;
        await api.delete(`/admin/bookings/${id}`);
        load();
    };

    const importIcal = async () => {
        if (!icalUrl) return;
        try {
            const r = await api.post("/admin/import-ical", { url: icalUrl });
            toast.success(`Importováno: ${r.data.imported} událostí`);
            setIcalUrl("");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const icalExportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/accommodation/ical.ics`;

    const newCount = messages.filter((m) => m.status === "new").length;

    return (
        <div className="min-h-screen bg-cream2">
            <header className="bg-cream border-b border-ink/15 px-6 md:px-12 py-5 flex items-center justify-between">
                <div className="flex items-baseline gap-3">
                    <Link to="/" className="font-serif text-2xl text-ink">Štrokay</Link>
                    <span className="overline">Admin</span>
                </div>
                <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink2 hover:text-burgundy">
                    <LogOut size={14} /> Odhlásit
                </button>
            </header>

            <div className="px-6 md:px-12 py-10 max-w-[1400px] mx-auto">
                <div className="flex gap-1 border-b border-ink/15 mb-10 overflow-x-auto">
                    {[
                        { k: "revenue", l: "Tržby" },
                        { k: "orders", l: "Objednávky" },
                        { k: "staff", l: "Obsluha" },
                        { k: "tables", l: "Stoly" },
                        { k: "extras", l: "Sortiment" },
                        { k: "wines", l: "Vína" },
                        { k: "notes", l: "Poznámky" },
                        { k: "messages", l: `Zprávy${newCount ? ` (${newCount})` : ""}` },
                        { k: "calendar", l: "Ubytování" },
                        { k: "ical", l: "iCal sync" },
                    ].map((t) => (
                        <button
                            key={t.k}
                            data-testid={`admin-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                                tab === t.k ? "border-burgundy text-burgundy" : "border-transparent text-ink2 hover:text-ink"
                            }`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>

                {tab === "revenue" && revenue && (
                    <div data-testid="admin-revenue" className="space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { k: "today", l: "Dnes" },
                                { k: "week", l: "Posledních 7 dní" },
                                { k: "month", l: "Posledních 30 dní" },
                                { k: "total", l: "Celkem" },
                            ].map((c) => (
                                <div key={c.k} className="p-6 bg-cream border border-ink/10">
                                    <p className="overline">{c.l}</p>
                                    <p className="font-serif text-3xl md:text-4xl text-ink mt-2">{revenue[c.k].toLocaleString("cs-CZ")} <span className="text-lg text-ink2">Kč</span></p>
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="p-6 bg-cream border border-ink/10">
                                <div className="flex items-center gap-2 mb-4"><Users size={16} className="text-burgundy" /><h3 className="font-serif text-xl">Tržby podle obsluhy</h3></div>
                                {revenue.by_staff.length === 0 && <p className="text-ink2 text-sm">Zatím žádné zaplacené objednávky.</p>}
                                <ul className="space-y-2">
                                    {revenue.by_staff.map((s) => (
                                        <li key={s.name} className="flex items-center justify-between text-sm border-b border-ink/10 py-2">
                                            <span>{s.name}</span>
                                            <span><b>{s.revenue.toLocaleString("cs-CZ")} Kč</b> <span className="text-ink2">/ {s.orders} obj.</span></span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="p-6 bg-cream border border-ink/10">
                                <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-burgundy" /><h3 className="font-serif text-xl">Top produkty</h3></div>
                                {revenue.top_products.length === 0 && <p className="text-ink2 text-sm">Zatím žádné prodeje.</p>}
                                <ul className="space-y-2">
                                    {revenue.top_products.map((p) => (
                                        <li key={p.name} className="flex items-center justify-between text-sm border-b border-ink/10 py-2">
                                            <span>{p.name} <span className="text-ink2">× {p.qty}</span></span>
                                            <b>{p.revenue.toLocaleString("cs-CZ")} Kč</b>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "orders" && (
                    <div data-testid="admin-orders" className="bg-cream border border-ink/10">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-ink/15 text-left">
                                <th className="p-3 overline">Stůl</th>
                                <th className="p-3 overline">Obsluha</th>
                                <th className="p-3 overline">Otevřeno</th>
                                <th className="p-3 overline">Stav</th>
                                <th className="p-3 overline text-right">Celkem</th>
                                <th className="p-3 overline"></th>
                            </tr></thead>
                            <tbody>
                                {orders.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ink2">Zatím žádné objednávky.</td></tr>}
                                {orders.map((o) => (
                                    <tr key={o.id} className="border-b border-ink/8 hover:bg-cream2">
                                        <td className="p-3 font-serif text-lg">{o.table_label}</td>
                                        <td className="p-3">{o.staff_name}</td>
                                        <td className="p-3 text-ink2">{new Date(o.opened_at).toLocaleString("cs-CZ")}</td>
                                        <td className="p-3">
                                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${
                                                o.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                                                o.status === "open" ? "bg-amber-100 text-amber-800" : "bg-ink/10 text-ink2"
                                            }`}>{o.status === "paid" ? "Zaplaceno" : o.status === "open" ? "Otevřeno" : "Zrušeno"}</span>
                                        </td>
                                        <td className="p-3 text-right font-semibold">{o.subtotal.toLocaleString("cs-CZ")} Kč</td>
                                        <td className="p-3 text-right">
                                            {o.status === "paid" && (
                                                <button data-testid={`admin-order-receipt-${o.id}`} onClick={() => downloadReceipt(o, o.staff_name)}
                                                    className="text-burgundy hover:text-burgundyDark" title="Stáhnout účtenku">
                                                    <FileText size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "staff" && (
                    <div data-testid="admin-staff" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4">Přidat brigádnici</h3>
                            <label className="block mb-3"><span className="overline">Jméno</span>
                                <input className="bottom-input mt-1" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} placeholder="Veronika" data-testid="admin-staff-new-name" /></label>
                            <label className="block mb-3"><span className="overline">Přihlašovací jméno</span>
                                <input className="bottom-input mt-1" value={newStaff.username} onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })} placeholder="veronika" data-testid="admin-staff-new-username" /></label>
                            <label className="block mb-3"><span className="overline">Heslo</span>
                                <input type="text" className="bottom-input mt-1" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} data-testid="admin-staff-new-password" /></label>
                            <label className="block mb-6"><span className="overline">Barva</span>
                                <input type="color" className="mt-2 w-16 h-10 border border-ink/15" value={newStaff.color} onChange={(e) => setNewStaff({ ...newStaff, color: e.target.value })} data-testid="admin-staff-new-color" /></label>
                            <button className="btn-burgundy w-full" data-testid="admin-staff-add"
                                onClick={async () => {
                                    if (!newStaff.name || !newStaff.username || !newStaff.password) { toast.error("Vyplňte všechna pole"); return; }
                                    try {
                                        await api.post("/admin/staff", newStaff);
                                        toast.success("Brigádnice přidána");
                                        setNewStaff({ name: "", username: "", password: "", color: "#5B7CB7" });
                                        load();
                                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                                }}>Přidat</button>
                        </div>
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4">Seznam ({staff.length})</h3>
                            {staff.length === 0 && <p className="text-ink2 text-sm">Žádné brigádnice.</p>}
                            <ul className="space-y-2">
                                {staff.map((s) => (
                                    <li key={s.id} className="flex items-center justify-between border-b border-ink/10 py-2 text-sm">
                                        <div className="flex items-center gap-3">
                                            <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                                            <div>
                                                <div className="text-ink"><b>{s.name}</b> <span className="text-ink2 font-mono text-xs">({s.username})</span></div>
                                                <div className="text-[10px] uppercase tracking-[0.18em] text-ink2">{s.active ? "aktivní" : "deaktivována"}</div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button title="Aktivovat/deaktivovat" onClick={async () => { await api.put(`/admin/staff/${s.id}`, { active: !s.active }); load(); }} className="p-2 hover:bg-cream2 text-ink2"><Check size={14} /></button>
                                            <button data-testid={`admin-staff-delete-${s.id}`} onClick={async () => { if (window.confirm(`Smazat ${s.name}?`)) { await api.delete(`/admin/staff/${s.id}`); load(); } }} className="p-2 hover:bg-cream2 text-burgundy"><Trash2 size={14} /></button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "tables" && (
                    <div data-testid="admin-tables" className="p-6 bg-cream border border-ink/10">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-serif text-2xl flex items-center gap-2"><MapPin size={18} /> Stoly ({tables.length})</h3>
                            <button className="btn-outline" style={{ padding: "0.6rem 1.2rem" }} data-testid="admin-table-add"
                                onClick={async () => {
                                    const label = window.prompt("Označení stolu (např. S7):");
                                    if (!label) return;
                                    await api.post("/admin/tables", { label, x: 50, y: 50, seats: 4 });
                                    load();
                                }}>+ Přidat stůl</button>
                        </div>
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-ink/15 text-left">
                                <th className="p-2 overline">Stůl</th>
                                <th className="p-2 overline">X %</th>
                                <th className="p-2 overline">Y %</th>
                                <th className="p-2 overline">Míst</th>
                                <th className="p-2"></th>
                            </tr></thead>
                            <tbody>
                                {tables.map((t) => (
                                    <tr key={t.id} className="border-b border-ink/8">
                                        <td className="p-2 font-serif text-lg">{t.label}</td>
                                        <td className="p-2"><input type="number" min="0" max="100" defaultValue={t.x} onBlur={async (e) => { await api.put(`/admin/tables/${t.id}`, { x: +e.target.value }); load(); }} className="w-16 bg-cream2 p-1 border border-ink/10" /></td>
                                        <td className="p-2"><input type="number" min="0" max="100" defaultValue={t.y} onBlur={async (e) => { await api.put(`/admin/tables/${t.id}`, { y: +e.target.value }); load(); }} className="w-16 bg-cream2 p-1 border border-ink/10" /></td>
                                        <td className="p-2"><input type="number" min="1" max="20" defaultValue={t.seats} onBlur={async (e) => { await api.put(`/admin/tables/${t.id}`, { seats: +e.target.value }); load(); }} className="w-16 bg-cream2 p-1 border border-ink/10" /></td>
                                        <td className="p-2 text-right"><button data-testid={`admin-table-delete-${t.label}`} onClick={async () => { if (window.confirm(`Smazat stůl ${t.label}?`)) { await api.delete(`/admin/tables/${t.id}`); load(); } }} className="p-2 hover:bg-cream2 text-burgundy"><Trash2 size={14} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "extras" && (
                    <div data-testid="admin-extras" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Package size={16} /> Přidat položku</h3>
                            <label className="block mb-3"><span className="overline">Název</span>
                                <input className="bottom-input mt-1" value={newExtra.name} onChange={(e) => setNewExtra({ ...newExtra, name: e.target.value })} data-testid="admin-extra-new-name" /></label>
                            <label className="block mb-3"><span className="overline">Cena (Kč)</span>
                                <input type="number" className="bottom-input mt-1" value={newExtra.price} onChange={(e) => setNewExtra({ ...newExtra, price: e.target.value })} data-testid="admin-extra-new-price" /></label>
                            <label className="block mb-6"><span className="overline">Kategorie</span>
                                <select className="mt-2 w-full p-2 border border-ink/15 bg-cream" value={newExtra.category} onChange={(e) => setNewExtra({ ...newExtra, category: e.target.value })} data-testid="admin-extra-new-cat">
                                    <option>nápoje</option><option>chuťovky</option><option>ostatní</option>
                                </select></label>
                            <button className="btn-burgundy w-full" data-testid="admin-extra-add"
                                onClick={async () => {
                                    if (!newExtra.name || !newExtra.price) { toast.error("Vyplňte název a cenu"); return; }
                                    await api.post("/admin/extras", { ...newExtra, price: +newExtra.price });
                                    setNewExtra({ name: "", price: "", category: "nápoje" });
                                    load();
                                }}>Přidat</button>
                        </div>
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4">Sortiment ({extras.length})</h3>
                            <ul className="space-y-2">
                                {extras.map((e) => (
                                    <li key={e.id} className="flex items-center justify-between border-b border-ink/10 py-2 text-sm">
                                        <div>
                                            <div className="text-ink">{e.name}</div>
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-ink2">{e.category} · {e.price} Kč</div>
                                        </div>
                                        <button data-testid={`admin-extra-delete-${e.id}`} onClick={async () => { if (window.confirm(`Smazat ${e.name}?`)) { await api.delete(`/admin/extras/${e.id}`); load(); } }} className="p-2 hover:bg-cream2 text-burgundy"><Trash2 size={14} /></button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "wines" && (
                    <div data-testid="admin-wines" className="p-6 bg-cream border border-ink/10">
                        <h3 className="font-serif text-2xl mb-4">Vína ({wines.length}) · ceny k úpravě</h3>
                        <p className="text-sm text-ink2 mb-6">Změna ceny se uloží okamžitě po opuštění pole (blur). Katalog na webu i v POS systému se aktualizuje automaticky.</p>
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-ink/15 text-left">
                                <th className="p-2 overline">Víno</th>
                                <th className="p-2 overline">Ročník</th>
                                <th className="p-2 overline">Sladkost</th>
                                <th className="p-2 overline text-right">Cena (Kč)</th>
                            </tr></thead>
                            <tbody>
                                {wines.map((w) => (
                                    <tr key={w.id} className="border-b border-ink/8">
                                        <td className="p-2 font-serif text-base text-ink">{w.name}</td>
                                        <td className="p-2 text-ink2">{w.year}</td>
                                        <td className="p-2 text-ink2">{w.sweetness}</td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                min="1"
                                                defaultValue={w.price}
                                                data-testid={`admin-wine-price-${w.id}`}
                                                onBlur={async (e) => {
                                                    const newPrice = parseInt(e.target.value, 10);
                                                    if (isNaN(newPrice) || newPrice <= 0 || newPrice === w.price) return;
                                                    try {
                                                        await api.put(`/admin/wines/${w.id}`, { price: newPrice });
                                                        toast.success(`${w.name}: ${newPrice} Kč`);
                                                        load();
                                                    } catch (err) {
                                                        toast.error(formatApiError(err.response?.data?.detail));
                                                    }
                                                }}
                                                className="w-24 p-2 bg-cream2 border border-ink/15 text-right font-semibold"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "notes" && (
                    <div data-testid="admin-notes" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><StickyNote size={16} /> Rychlé poznámky</h3>
                            <div className="flex gap-2 mb-4">
                                <input className="bottom-input flex-1" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Nová poznámka…" data-testid="admin-note-new"
                                    onKeyDown={async (ev) => { if (ev.key === "Enter" && newNote.trim()) { await api.post("/admin/notes", { text: newNote }); setNewNote(""); load(); } }} />
                                <button className="btn-burgundy" style={{ padding: "0.7rem 1.2rem" }} data-testid="admin-note-add"
                                    onClick={async () => { if (!newNote.trim()) return; await api.post("/admin/notes", { text: newNote }); setNewNote(""); load(); }}>+</button>
                            </div>
                            <ul className="space-y-1">
                                {notes.length === 0 && <p className="text-ink2 text-sm">Žádné poznámky. Klidně sem napiš, co si potřebuješ pamatovat.</p>}
                                {notes.map((n) => (
                                    <li key={n.id} className="flex items-start gap-3 py-2 border-b border-ink/8 group">
                                        <button data-testid={`admin-note-toggle-${n.id}`} onClick={async () => { await api.put(`/admin/notes/${n.id}`, { done: !n.done }); load(); }} className="mt-0.5">
                                            {n.done ? <CheckSquare size={16} className="text-burgundy" /> : <Square size={16} className="text-ink2" />}
                                        </button>
                                        <span className={`flex-1 text-sm ${n.done ? "line-through text-ink2" : "text-ink"}`}>{n.text}</span>
                                        <button data-testid={`admin-note-delete-${n.id}`} onClick={async () => { await api.delete(`/admin/notes/${n.id}`); load(); }} className="opacity-0 group-hover:opacity-100 text-burgundy"><X size={14} /></button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-6 bg-cream border border-ink/10">
                            <h3 className="font-serif text-xl mb-4">Poznámky k rezervacím ubytování</h3>
                            {bookings.length === 0 && <p className="text-ink2 text-sm">Žádné rezervace.</p>}
                            <ul className="space-y-3 max-h-[520px] overflow-auto">
                                {bookings.map((b) => (
                                    <li key={b.id} className="border-b border-ink/10 pb-3">
                                        <div className="text-sm text-ink"><b>{b.start_date} → {b.end_date}</b> <span className="text-[10px] uppercase tracking-[0.18em] text-ink2 ml-2">{b.source}</span></div>
                                        <textarea rows={2} defaultValue={b.note || ""} placeholder="Poznámka k této rezervaci…"
                                            className="w-full mt-2 p-2 border border-ink/15 bg-cream2 text-sm resize-none"
                                            data-testid={`admin-booking-note-${b.id}`}
                                            onBlur={async (e) => { if (e.target.value !== (b.note || "")) { await api.put(`/admin/bookings/${b.id}/note`, { note: e.target.value }); toast.success("Poznámka uložena"); load(); } }} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "messages" && (
                    <div data-testid="admin-messages" className="space-y-4">
                        {messages.length === 0 && <p className="text-ink2">Žádné zprávy.</p>}
                        {messages.map((m) => (
                            <div key={m.id} className={`p-6 bg-cream border ${m.status === "new" ? "border-burgundy/40" : "border-ink/15"}`}>
                                <div className="flex flex-wrap items-start gap-4 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="font-serif text-2xl text-ink">{m.name}</span>
                                            {m.status === "new" && <span className="text-[10px] uppercase tracking-[0.2em] bg-burgundy text-cream px-2 py-1">Nová</span>}
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-ink2 border border-ink/20 px-2 py-1">{m.purpose}</span>
                                        </div>
                                        <div className="text-sm text-ink2">
                                            <a href={`mailto:${m.email}`} className="hover:text-burgundy">{m.email}</a>
                                            {m.phone ? ` · ${m.phone}` : ""} · {new Date(m.created_at).toLocaleString("cs-CZ")}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {m.status === "new" ? (
                                            <button data-testid={`admin-msg-read-${m.id}`} onClick={() => markRead(m.id)} className="p-2 hover:bg-cream2" title="Označit jako přečteno">
                                                <Mail size={16} />
                                            </button>
                                        ) : (
                                            <button className="p-2 text-ink2" disabled>
                                                <MailOpen size={16} />
                                            </button>
                                        )}
                                        <button data-testid={`admin-msg-delete-${m.id}`} onClick={() => delMsg(m.id)} className="p-2 hover:bg-cream2 text-burgundy">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-ink whitespace-pre-wrap text-sm leading-relaxed mt-2">{m.message}</p>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "calendar" && (
                    <div data-testid="admin-calendar" className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        <div className="bg-cream p-6 border border-ink/15">
                            <h3 className="font-serif text-2xl text-ink mb-4 flex items-center gap-2"><Plus size={18} /> Přidat obsazený termín</h3>
                            <div className="flex justify-center mb-4">
                                <Calendar
                                    mode="range"
                                    selected={range}
                                    onSelect={setRange}
                                    numberOfMonths={1}
                                    weekStartsOn={1}
                                />
                            </div>
                            <button data-testid="admin-add-booking" onClick={addBooking} className="btn-burgundy w-full">Přidat termín</button>
                        </div>
                        <div className="bg-cream p-6 border border-ink/15">
                            <h3 className="font-serif text-2xl text-ink mb-4">Obsazené termíny ({bookings.length})</h3>
                            <ul className="space-y-2 max-h-[480px] overflow-auto">
                                {bookings.length === 0 && <p className="text-ink2 text-sm">Žádné obsazené termíny.</p>}
                                {bookings.map((b) => (
                                    <li key={b.id} className="flex items-center justify-between border-b border-ink/10 py-2 text-sm">
                                        <div>
                                            <span className="font-medium text-ink">{b.start_date} → {b.end_date}</span>
                                            <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-ink2">{b.source}</span>
                                            <div className="text-ink2 text-xs">{b.summary}</div>
                                        </div>
                                        <button data-testid={`admin-booking-delete-${b.id}`} onClick={() => delBooking(b.id)} className="text-burgundy p-2 hover:bg-cream2">
                                            <Trash2 size={14} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "ical" && (
                    <div data-testid="admin-ical" className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-4xl">
                        <div className="bg-cream p-6 border border-ink/15">
                            <h3 className="font-serif text-2xl text-ink mb-3 flex items-center gap-2"><Download size={18} /> Export</h3>
                            <p className="text-sm text-ink2 mb-4">Sdílejte tuto adresu s Airbnb, Booking.com nebo Google Calendar pro automatickou synchronizaci.</p>
                            <div className="bg-cream2 p-3 break-all text-xs font-mono text-ink mb-4 select-all">{icalExportUrl}</div>
                            <button
                                data-testid="admin-ical-copy"
                                onClick={() => { navigator.clipboard.writeText(icalExportUrl); toast.success("Zkopírováno"); }}
                                className="btn-outline w-full"
                            >
                                Kopírovat odkaz
                            </button>
                        </div>
                        <div className="bg-cream p-6 border border-ink/15">
                            <h3 className="font-serif text-2xl text-ink mb-3">Import</h3>
                            <p className="text-sm text-ink2 mb-4">Vložte iCal URL z Airbnb, Booking.com atd. Importované termíny se přepíší při dalším importu.</p>
                            <input
                                data-testid="admin-ical-input"
                                type="url"
                                value={icalUrl}
                                onChange={(e) => setIcalUrl(e.target.value)}
                                placeholder="https://www.airbnb.com/calendar/ical/..."
                                className="bottom-input mb-4"
                            />
                            <button data-testid="admin-ical-import" onClick={importIcal} className="btn-burgundy w-full">Importovat</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminPage() {
    const [authed, setAuthed] = useState(!!localStorage.getItem("strokay_token") && localStorage.getItem("strokay_role") === "admin");

    useEffect(() => {
        if (!authed) return;
        api.get("/auth/me").then((r) => {
            if (r.data.role !== "admin") {
                localStorage.removeItem("strokay_token");
                setAuthed(false);
            }
        }).catch(() => {
            localStorage.removeItem("strokay_token");
            setAuthed(false);
        });
    }, [authed]);

    const logout = () => {
        localStorage.removeItem("strokay_token");
        localStorage.removeItem("strokay_email");
        localStorage.removeItem("strokay_role");
        setAuthed(false);
    };

    return authed ? <Panel onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
